import PropTypes from "prop-types";
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useDeviceInfo, window } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { Adjoin, adjoinPropTypes } from "../Adjoin";
import { CaptionPointer } from "../CaptionPointer";
import { CloseButton } from "../CloseButton";
import { ConfigurationContext } from "../ConfigurationContext";
import { FocusContainer } from "../FocusContainer";
import { Shadow } from "../Shadow";
import { Show } from "../Show";
import styles from "./Caption.module.scss";

const CARET_CENTER = 12;
const CARET_WIDTH = 24;
const FIRST_FOCUSABLE_INDEX_WITH_CLOSE_BUTTON = 2;
const FIRST_FOCUSABLE_INDEX_WITHOUT_CLOSE_BUTTON = 1;

/**
 * Caption uses Adjoin to display a styled overlay next to another element which contains Caption's
 * child content. Caption typically displays a "pointer" to show the correlation to the main content,
 * but can be turned off. The child content will be invisible and will ignore mouse events when
 * location is hidden.
 */
export const Caption = (props) => {
  const {
    adjoinedContentClassName,
    adjoiningContent,
    adjustToFit,
    alignment,
    bestFit = true,
    captionRef,
    children,
    className,
    constrainFocus = true,
    doc,
    disableInitialFocus,
    duration,
    id,
    inline = true,
    location = "hidden",
    mainClassName,
    onClick,
    onClose,
    onTransformationEnd,
    pointerAlignment = "center",
    pointerXOffset = 0,
    pointerYOffset = 0,
    portal = false,
    showClose = false,
    showPointer = true,
    stackingContext = true,
    width,
    xOffset = 0,
    yOffset = 0,
  } = props;
  const { gridPadding } = useDeviceInfo();
  const [adjustments, setAdjustments] = useState({ x: 0, y: 0 });
  const [currentAlignment, setCurrentAlignment] = useState(alignment);
  const [currentLocation, setCurrentLocation] = useState(location);
  const [currentPointerAlignment, setCurrentPointerAlignment] = useState(pointerAlignment);
  const [isClosed, setIsClosed] = useState(location === "hidden");
  const [pointerOffsets, setPointerOffsets] = useState([0, 0]);
  const [needsLocationRecalc, setNeedsLocationRecalc] = useState(false);
  const adjoiningContentContainer = useRef(null);
  const configurationContext = useContext(ConfigurationContext) ?? {};
  const mainContentContainer = useRef(null);
  const previousLocation = useRef(null);

  useEffect(() => {
    previousLocation.current = currentLocation;
    setCurrentLocation(getBestLocation());
    setAdjustments({ x: 0, y: 0 });
    setIsClosed(location === "hidden" && previousLocation.current === "hidden");

    if (location === "hidden") {
      setPointerOffsets([0, 0]);
    } else {
      setNeedsLocationRecalc(true);
    }
  }, [location]);

  useLayoutEffect(() => {
    if (needsLocationRecalc && adjoiningContentContainer.current && mainContentContainer.current) {
      setCurrentLocation(getBestLocation());
      setNeedsLocationRecalc(false);
    }
  }, [needsLocationRecalc]);

  useEffect(() => {
    setCurrentPointerAlignment(pointerAlignment);
  }, [pointerAlignment]);

  useEffect(() => {
    if (currentAlignment !== alignment) {
      setCurrentAlignment(alignment);
    }
  }, [alignment]);

  useEffect(() => {
    const { handleCaption } = configurationContext ?? {};

    if (Array.isArray(handleCaption) && handleCaption.length) {
      handleCaption.forEach((handler) => {
        handler({
          adjoinedContent: adjoiningContentContainer.current,
          mainContent: mainContentContainer.current,
          open: currentLocation !== "hidden",
        });
      });
    }
  }, [currentLocation]);

  return (
    <div {...getProps()}>
      <Adjoin {...getAdjoinProps()} adjoinRef={captionRef}>
        <div className={getCaptionTriggerClass()} ref={mainContentContainer}>
          {children}
        </div>
      </Adjoin>
    </div>
  );

  function renderCaption() {
    const content = constrainFocus ? (
      <FocusContainer {...getFocusContainerProps()}>{renderAdjoinedContent()}</FocusContainer>
    ) : (
      renderAdjoinedContent()
    );

    return <Show {...getShowProps()}>{content}</Show>;
  }

  function renderAdjoinedContent() {
    return (
      !isClosed &&
      (canPointerBeShown() ? (
        <Adjoin {...getPointerAdjoinProps()}>{renderContent()}</Adjoin>
      ) : (
        renderContent()
      ))
    );
  }

  function renderContent() {
    return (
      <Shadow>
        <div {...getContentProps()}>
          {showClose && <CloseButton {...getCloseButtonProps()} />}
          {adjoiningContent}
        </div>
      </Shadow>
    );
  }

  function renderPointer() {
    return <CaptionPointer direction={getPointerDirection()} />;
  }

  function getContentProps() {
    const fullScreenStyling =
      previousLocation.current === "full-screen" || currentLocation === "full-screen"
        ? {
            style: {
              height: getViewportHeight() - gridPadding * 2,
              width: getViewportWidth() - gridPadding * 2,
            },
          }
        : {};
    const contentProps = {
      className: getAdjoiningClassName(),
      ref: getContentCallbackRef,
      ...fullScreenStyling,
    };

    return props?.["aria-label"] || props?.["aria-labelledby"]
      ? {
          ...contentProps,
          "aria-describedby": props["aria-describedby"],
          "aria-label": props["aria-label"],
          "aria-labelledby": props["aria-labelledby"],
          "aria-modal": "true",
          role: "dialog",
        }
      : contentProps;
  }

  function getContentCallbackRef() {
    const newXOffset = getPointerXoffset();
    const newYOffset = getPointerYoffset();

    if (pointerOffsets[0] !== newXOffset || pointerOffsets[1] !== newYOffset) {
      setPointerOffsets([newXOffset, newYOffset]);
    }
  }

  function getProps() {
    return {
      className: classNames(className, {
        [styles.hidden]: !children && currentLocation === "hidden",
      }),
      onClick,
    };
  }

  function getAdjoinProps() {
    return {
      adjoinedClassName: classNames({
        [styles.hiddenAdjoin]: currentLocation === "hidden",
        [styles.stackingContext]: stackingContext,
      }),
      adjoinedContentClassName,
      adjoiningContent: renderCaption(),
      adjustToFit,
      adjustToFitCallback,
      alignment: currentAlignment,
      id,
      immediate: true,
      inline,
      location: currentLocation,
      mainClassName,
      portal,
      xOffset: getXOffset(),
      yOffset: getYOffset(),
    };
  }

  function getFocusContainerProps() {
    return {
      "aria-hidden": props["aria-hidden"] || currentLocation === "hidden",
      className: styles.adjoinContainer,
      defaultFocusIndex: showClose
        ? FIRST_FOCUSABLE_INDEX_WITH_CLOSE_BUTTON
        : FIRST_FOCUSABLE_INDEX_WITHOUT_CLOSE_BUTTON,
      disableInitialFocus,
      doc,
      revealed: currentLocation !== "hidden",
    };
  }

  function getAdjoiningClassName() {
    return classNames(styles.mainContent, {
      [styles.widthExtraLarge]: width === "xlarge",
      [styles.widthExtraSmall]: width === "xsmall",
      [styles.widthLarge]: width === "large",
      [styles.widthMedium]: width === "medium",
      [styles.widthSmall]: width === "small",
    });
  }

  function getCaptionTriggerClass() {
    return classNames({
      [styles.captionTrigger]: currentLocation !== "hidden",
    });
  }

  function getPointerAdjoinProps() {
    return {
      adjoinedClassName: styles.pointer,
      adjoiningContent: renderPointer(),
      adjustToFit: false,
      alignment: getPointerAlignment(),
      immediate: true,
      location: getPointerLocation(),
      xOffset: pointerOffsets[0],
      yOffset: pointerOffsets[1],
    };
  }

  function getShowProps() {
    return {
      className: classNames({
        [styles.fullWidth]: currentLocation === "full-screen",
      }),
      duration,
      id,
      location: currentLocation,
      onTransformationEnd: handleTransformationEnd,
      ref: adjoiningContentContainer,
    };
  }

  function getCloseButtonProps() {
    return {
      "aria-label": props["aria-label-close"],
      className: styles.closeButton,
      onClick: onClose,
    };
  }

  function handleTransformationEnd() {
    onTransformationEnd?.();

    if (currentLocation === "hidden" && previousLocation.current !== "hidden") {
      setIsClosed(true);
    }
  }

  function getViewportWidth() {
    return window.document.documentElement.clientWidth;
  }

  function getViewportHeight() {
    return window.document.documentElement.clientHeight;
  }

  function adjustToFitCallback(deltas) {
    setAdjustments(deltas);
  }

  function getBestLocation() {
    let bestLocation = location;

    if (bestFit && adjoiningContentContainer?.current && mainContentContainer?.current) {
      const adjoiningContentHeight =
        getAdjoinContentContainerHeight() +
        (canPointerBeShown() && (location === "above" || location === "below") ? CARET_WIDTH : 0);
      const adjoiningContentWidth = getAdjoinContentContainerWidth();
      const sizeAbove = getMainContentContainerY();
      const sizeBelow =
        window.document.documentElement.clientHeight -
        getMainContentContainerY() -
        getMainContentContainerHeight();
      const sizeLeft = getMainContentContainerX();
      const sizeRight =
        window.document.documentElement.clientWidth -
        getMainContentContainerX() -
        getMainContentContainerWidth();

      if (location === "above") {
        if (adjoiningContentHeight > sizeAbove && sizeAbove < sizeBelow) {
          bestLocation = "below";
        }
      } else if (location === "below") {
        if (adjoiningContentHeight > sizeBelow && sizeAbove > sizeBelow) {
          bestLocation = "above";
        }
      } else if (location === "left") {
        if (adjoiningContentWidth > sizeLeft && sizeLeft < sizeRight) {
          bestLocation = "right";
        }
      } else if (location === "right") {
        if (adjoiningContentWidth > sizeRight && sizeLeft > sizeRight) {
          bestLocation = "left";
        }
      }
    }

    return bestLocation || "below";
  }

  function getXOffset() {
    let offset = xOffset;

    if (currentLocation === "right") {
      offset += CARET_CENTER;
    } else if (currentLocation === "left") {
      offset -= CARET_CENTER;
    }

    return offset;
  }

  function getYOffset() {
    let offset = yOffset;

    if (currentLocation === "left" || currentLocation === "right") {
      if (currentAlignment === "bottom") {
        offset += CARET_WIDTH;
      } else if (currentAlignment === "center") {
        offset += CARET_CENTER;
      }
    } else if (currentLocation === "below") {
      offset += CARET_CENTER;
    } else if (currentLocation === "above") {
      offset -= CARET_CENTER;
    }

    return offset;
  }

  function getPointerAlignment() {
    let caretAlignment = currentPointerAlignment;

    if (currentPointerAlignment === "center") {
      if (currentLocation === "above" || currentLocation === "below") {
        caretAlignment = "left";
      } else if (currentLocation === "left" || currentLocation === "right") {
        caretAlignment = "top";
      } else {
        caretAlignment = undefined;
      }
    }

    return caretAlignment;
  }

  function getPointerLocation() {
    let pointerLocation;

    if (currentLocation === "below") {
      pointerLocation = "above";
    } else if (currentLocation === "left") {
      pointerLocation = "right";
    } else if (currentLocation === "right") {
      pointerLocation = "left";
    } else if (currentLocation === "above") {
      pointerLocation = "below";
    }

    if (location === "hidden") {
      pointerLocation = "hidden";
    }

    return pointerLocation;
  }

  function getPointerDirection() {
    let direction = "hidden";

    if (currentLocation === "below") {
      direction = "up";
    } else if (currentLocation === "left") {
      direction = "right";
    } else if (currentLocation === "right") {
      direction = "left";
    } else if (currentLocation === "above") {
      direction = "down";
    }

    return direction;
  }

  function getPointerXoffset() {
    let offset = pointerXOffset + adjustments.x;

    if (
      adjoiningContentContainer?.current &&
      mainContentContainer?.current &&
      (currentLocation === "above" || currentLocation === "below")
    ) {
      const adjoinContentWidth = getAdjoinContentContainerWidth();
      const mainContentWidth = getMainContentContainerWidth();

      if (currentAlignment === "left") {
        if (currentPointerAlignment === "right") {
          offset += mainContentWidth - adjoinContentWidth;
        } else if (currentPointerAlignment === "center") {
          offset += mainContentWidth / 2;
        }
      } else if (currentAlignment === "center") {
        if (currentPointerAlignment === "left") {
          offset -= (mainContentWidth - adjoinContentWidth) / 2;
        } else if (currentPointerAlignment === "right") {
          if (mainContentWidth <= adjoinContentWidth) {
            offset += (mainContentWidth - adjoinContentWidth) / 2;
          }
        } else if (currentPointerAlignment === "center") {
          offset +=
            mainContentWidth / 2 - (mainContentWidth - adjoinContentWidth) / 2 - CARET_CENTER;
        }
      } else {
        if (currentPointerAlignment === "left") {
          offset -= mainContentWidth - adjoinContentWidth;
        } else if (currentPointerAlignment === "center") {
          offset += mainContentWidth / 2 - (mainContentWidth - adjoinContentWidth) - CARET_CENTER;
        }
      }
    }

    if (currentLocation === "left") {
      offset -= 1;
    } else if (currentLocation === "right") {
      offset += 1;
    }

    return offset;
  }

  function getPointerYoffset() {
    let offset = pointerYOffset;

    if (
      adjoiningContentContainer?.current &&
      mainContentContainer?.current &&
      (currentLocation === "left" || currentLocation === "right")
    ) {
      const adjoinContentHeight = getAdjoinContentContainerHeight();
      const mainContentHeight = getMainContentContainerHeight();

      if (currentAlignment === "top") {
        if (currentPointerAlignment === "bottom") {
          offset += mainContentHeight - adjoinContentHeight;
        } else if (currentPointerAlignment === "center") {
          offset += mainContentHeight / 2 - CARET_CENTER;
        }
      } else if (currentAlignment === "center") {
        if (currentPointerAlignment === "top") {
          offset -= (mainContentHeight - adjoinContentHeight) / 2;
        } else if (currentPointerAlignment === "bottom") {
          offset += (mainContentHeight - adjoinContentHeight) / 2;
        } else if (currentPointerAlignment === "center") {
          offset +=
            mainContentHeight / 2 - (mainContentHeight - adjoinContentHeight) / 2 - CARET_WIDTH;
        }
      } else {
        if (currentPointerAlignment === "top") {
          offset -= mainContentHeight - adjoinContentHeight;
        } else if (currentPointerAlignment === "center") {
          offset += adjoinContentHeight - mainContentHeight / 2 - CARET_CENTER;
        }
      }
    }

    if (currentLocation === "above") {
      offset -= 1;
    } else if (currentLocation === "below") {
      offset += 1;
    }

    return offset;
  }

  function getAdjoinContentContainerHeight() {
    return adjoiningContentContainer.current.getBoundingClientRect().height;
  }

  function getAdjoinContentContainerWidth() {
    return adjoiningContentContainer.current.getBoundingClientRect().width;
  }

  function getMainContentContainerHeight() {
    return mainContentContainer.current.getBoundingClientRect().height;
  }

  function getMainContentContainerWidth() {
    return mainContentContainer.current.getBoundingClientRect().width;
  }

  function getMainContentContainerX() {
    return mainContentContainer.current.getBoundingClientRect().x;
  }

  function getMainContentContainerY() {
    return mainContentContainer.current.getBoundingClientRect().y;
  }

  function canPointerBeShown() {
    return (
      showPointer &&
      (currentLocation === "above" ||
        currentLocation === "below" ||
        currentLocation === "left" ||
        currentLocation === "right")
    );
  }
};

Caption.displayName = "Caption";

export const captionPropTypes = {
  /** Content that will be rendered adjacent to "main" content. */
  adjoiningContent: PropTypes.node,

  /** Whether to use Adjoin adjustment logic when positioning Caption. See Adjoin for details. */
  adjustToFit: PropTypes.bool,

  /** Where adjoiningContent will be placed. */
  alignment: adjoinPropTypes.alignment,

  /** aria-describedby text to provide accessibility description for Caption. */
  "aria-describedby": PropTypes.string,

  /** Set the aria-hidden attribute to be passed down the container. */
  "aria-hidden": PropTypes.bool,

  /** aria-label text to provide accessibility description for Caption. */
  "aria-label": PropTypes.string,

  /** aria-label text to provide additional accessibility description of close button. */
  "aria-label-close": PropTypes.string,

  /** aria-labelledby text to provide accessibility label for Caption. */
  "aria-labelledby": PropTypes.string,

  /**
   * Caption will attempt to place the caption content where directed by the location prop, but if
   * the content will not fit, Caption will move it to the other size. For example, if location is
   * defined as below and there's not enough room underneath, and there's more more on the screen
   * above the child content, the caption will be placed above.
   */
  bestFit: PropTypes.bool,

  /** Ref passed to allow useCaption to close Caption on clicking outside of component. */
  captionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),

  /** Content that will be rendered when Caption is displayed. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Caption is able to constrain focus to the flyout if desired. When set, the traveler, cannot set
   * focus on an element outside the flyout using tab and shift-tab.
   */
  constrainFocus: PropTypes.bool,

  /**
   * By default this will the global document object, but can be overridden to support iFrames. This
   * is used by Caption to get a reference to a DOM element.
   */
  doc: PropTypes.object,

  /** Length of duration for the "main" content to be positioned. */
  duration: PropTypes.number,

  /** Unique identifier which is passed to Show when it's necessary to force a re-render. */
  id: PropTypes.string,

  /** Whether to display children content inline. Defaults to true. */
  inline: PropTypes.bool,

  /** Defines which where the caption will be displayed in relation to the "main" child content. */
  location: adjoinPropTypes.location,

  /** className to be given to main content container. */
  mainClassName: PropTypes.string,

  /** Callback that will be called when caption is clicked. */
  onClick: PropTypes.func,

  /** Callback to receive notification of click to dismiss caption. */
  onClose: PropTypes.func,

  /** Option callback to inform when caption has been placed. */
  onTransformationEnd: PropTypes.func,

  /** Where adjoiningContent will be placed. */
  pointerAlignment: PropTypes.oneOf(["bottom", "left", "center", "right", "top"]),

  /** Positive or negative value to nudge the pointer horizontally. */
  pointerXOffset: adjoinPropTypes.xOffset,

  /** Positive or negative value to nudge the pointer in the vertical direction. */
  pointerYOffset: adjoinPropTypes.yOffset,

  /** Whether to render the adjoining content in a React portal. */
  portal: PropTypes.bool,

  /**
   * Present close button to dismiss caption. Caption will not close itself. The caller must
   * respond to the onClose event to set Caption's location prop to "hidden".
   */
  showClose: PropTypes.bool,

  /** Optional caret will be displayed to "tie" the caption to the main content. */
  showPointer: PropTypes.bool,

  /**
   * Option allows the caller to indicate if a new stacking order should be defined, which changes
   * z-index's effect.
   */
  stackingContext: PropTypes.bool,

  /** Width for adjoining content. If a width is not given, the width will be governed by content. */
  width: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),

  /** Positive or negative value to nudge the adjoining element horizontally. */
  xOffset: adjoinPropTypes.xOffset,

  /** Positive or negative value to nudge the adjoining element in the vertical direction. */
  yOffset: adjoinPropTypes.yOffset,
};

Caption.propTypes = captionPropTypes;
